// @flow
import * as React from 'react';
import type { Location, RouterHistory } from 'react-router';
import { sprintf } from 'sprintf-js';
import { trackClicked } from 'utils/analytics';
import { algoliaTrackClick } from 'utils/algoliaSearch';
import SearchFiltersSidebarContainer from 'containers/GradSchoolsSearchFiltersSidebarContainer';
import SaveListingContainer from 'containers/SaveListingContainer';
import Divider from 'components/Divider';
import SearchHit from 'components/GradSchoolsSearchHit';
import Pagination from 'components/Pagination';
import LoadingIndicator from 'components/LoadingIndicator';
import SearchEmptyResultsMessage from 'components/GradSchoolsSearchEmptyResultsMessage';
import type { State as MainSearch } from 'redux/main/reducers/mainSearch';
import scrollIntoView from 'utils/scrollIntoView';
import Flex from 'components/Flex';
import Box from 'components/Box';
import Hide from 'components/Hide';
import Text from 'components/Text';
import type { CurrentUser } from 'api/currentUser';
import { gutterWidth } from '../theme';
import { results as resultsFn } from '../redux/main/reducers/mainSearch';

type Props = {
  clearSearchLocation: Function,
  history: RouterHistory,
  location: Location,
  currentUser: CurrentUser,
  mainSearch: MainSearch,
  getSearchTrackingData: () => Object,
  updateContactedGradPrograms: Function
};

const ResultCount = ({ nbHits }: { nbHits: ?number }) => (
  <Text
    display={['block', 'block', 'block', 'inline-block']}
    textAlign={['center', 'center', 'center', 'left']}
    mb={['20px', '20px', '20px', 0]}
    fontSize={['20px', null, null, '16px']}
  >
    {typeof nbHits === 'number' &&
      (nbHits === 1
        ? /* TRANSLATORS: Grad School Home Page - search results description */

          getText('1 program found')
        : /* TRANSLATORS: Grad School Home Page - search results description */

          sprintf(getText('%(resultCount)s programs found'), {
            resultCount: nbHits.toLocaleString(USER_LOCALE)
          }))}
  </Text>
);

const GradSearchResults = ({
  clearSearchLocation,
  history,
  location,
  currentUser,
  mainSearch,
  getSearchTrackingData,
  updateContactedGradPrograms
}: Props) => {
  const scrollToId = location.state && location.state.scrollToId;
  const { query, queryID, isSearching, initialized } = mainSearch;
  const results = resultsFn(mainSearch);
  const { hits, nbHits, hitsPerPage, page, nbPages } = results || {};
  const onPageClick = () => scrollIntoView();

  const SearchResultsBody = () =>
    hits && hits.length > 0 && queryID ? (
      <>
        {results.hits.map((hit, index) => (
          <SaveListingContainer
            key={hit.objectID}
            listingId={hit.objectID}
            listingType={hit.type}
            listingName={hit.name}
            orgId={hit.orgID}
            orgType={hit.orgType}
            render={({ saveListing, unsaveListing, saved, canSave }) => {
              const hasBeenContacted = currentUser.contactedGradPrograms.includes(
                hit.objectID
              );
              return (
                <SearchHit
                  hit={hit}
                  scrollIntoView={hit.objectID === scrollToId}
                  history={history}
                  location={location}
                  saveProgram={saveListing}
                  unsaveProgram={unsaveListing}
                  currentUser={currentUser}
                  saved={saved}
                  canSave={canSave}
                  hasBeenContacted={hasBeenContacted}
                  updateContactedGradPrograms={updateContactedGradPrograms}
                  trackClick={() => {
                    algoliaTrackClick(
                      queryID,
                      hit.objectID,
                      // Position in algolia analytics is 1-indexed
                      hitsPerPage ? page * hitsPerPage + index + 1 : index + 1
                    );
                    trackClicked('Search Result', {
                      org_id: hit.orgID,
                      org_type: hit.orgType,
                      result_id: hit.objectID,
                      result_index: index,
                      result_type: hit.type,
                      ...getSearchTrackingData()
                    });
                  }}
                />
              );
            }}
          />
        ))}
        {typeof nbPages === 'number' &&
          typeof page === 'number' &&
          typeof hitsPerPage === 'number' && (
            <Pagination
              nbHits={nbHits}
              nbPages={nbPages}
              pageIndex={page}
              hitsPerPage={hitsPerPage}
              onClick={onPageClick}
            />
          )}
      </>
    ) : isSearching || !initialized ? (
      <LoadingIndicator />
    ) : (
      <SearchEmptyResultsMessage
        query={query}
        searchLocation={currentUser.searchLocation}
        clearSearchLocation={clearSearchLocation}
      />
    );
  return (
    <Box id="results" mx="auto" display="flex" flexWrap="wrap">
      <Box width={[1, 1, 1, 1 / 3]} px={[0, null, null, gutterWidth]}>
        {typeof nbHits === 'number' && nbHits > 0 && (
          <Hide lg xl>
            <ResultCount nbHits={nbHits} />
          </Hide>
        )}
        <SearchFiltersSidebarContainer />
      </Box>
      <Box
        width={[1, 1, 1, 2 / 3]}
        px={[gutterWidth / 2, null, null, gutterWidth]}
        data-qa-id="search-results"
      >
        <Hide xs sm md>
          {typeof nbHits === 'number' && nbHits > 0 && (
            <>
              <Flex alignItems="center">
                <ResultCount nbHits={nbHits} />
              </Flex>
              <Divider light mt={24} mb={32} />
            </>
          )}
        </Hide>
        <SearchResultsBody />
      </Box>
    </Box>
  );
};

export default GradSearchResults;
